import { Component, EventEmitter, Input, Output, ViewContainerRef } from '@angular/core';
import { TooltipPosition } from '@angular/material/tooltip';
import { HasOverlay, TsOverlayComponent } from '../ts-overlay';
import { MatMenuPanel } from '@angular/material/menu';

export type TsButtonVariant = 'primary' | 'secondary' | 'danger' | 'link' | 'dashed' | 'paginator';

@Component({
  selector: 'ts-button',
  templateUrl: './ts-button.component.html',
  styleUrls: ['./ts-button.component.scss'],
  // When fullWidth is set, opt the host out of its default fit-content width so it spans its container.
  host: { '[class.ts-button--block]': 'fullWidth', '[class.cursor-not-allowed]': 'disabled || loading' },
})
/**
 @deprecated we are transitioning to use the `variant` property to assign styles to the button
 */
export class TsButtonComponent {
  /** Default foreground for `type="icon"` when {@link color} is not set. */
  static readonly iconDefaultColor = 'rgb(24, 29, 31)';

  @Input() type: 'flat' | 'raised' | 'stroked' | 'icon' | 'fab' = 'flat';
  /**
   * @deprecated we are transitioning to use the `variant` property to assign styles to the button
   */
  @Input() color?: 'primary' | 'basic' | 'accent' | 'warn' | 'success';
  @Input() variant: TsButtonVariant;
  /** Stretch the button to fill the width of its container instead of hugging its content. */
  @Input() fullWidth = false;
  @Input() icon: string = '';
  /**
   * For `type="icon"`: Material svgIcon registry id (same as `<mat-icon [svgIcon]>`).
   * When set, it is passed to the inner `ts-icon` and overrides {@link icon} for glyph resolution.
   */
  @Input() svgIcon: string | undefined;
  @Input() menuIcon = false;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() debounceTime: number = 0;
  @Input() matMenuTriggerFor: MatMenuPanel;
  @Input() iconTooltip: string;
  /** Tooltip text for icon buttons (inner `mat-icon-button`). Use this instead of putting `matTooltip` on the host. */
  @Input() buttonTooltip: string;
  @Input() matTooltipPosition: TooltipPosition = 'below';
  @Input() matTooltipShowDelay: number = 0;
  @Input() active = false;
  @Input() iconCssClass;
  @Input() lucideIcon = false;
  @Input() allowBubbling = false;
  /**
   * When {@link lucideIcon} is true, passed to `lucide-icon` as `color` (SVG stroke). Overrides {@link lucideIconStroke} / prefix defaults when set.
   */
  @Input() lucideStrokeColor?: string;

  /**
   * @description
   * Overlay component to be displayed
   */
  @Input() tsOverlayTriggerFor: TsOverlayComponent | HasOverlay;
  @Input() iconColor;
  @Input() alwaysEmitClick = false;
  /**
   * Native HTML button type. When omitted, the attribute is not set (browser default applies).
   */
  @Input() htmlButtonType?: 'button' | 'submit' | 'reset';
  /**
   * Sets tabindex on the inner native button (e.g. -1 for modal close affordances).
   */
  @Input() tabIndex?: number;

  /**
   * Emits when the button is activated. Prefer this over `(click)` to avoid collision with the native DOM click event.
   * When tsOverlayTriggerFor is set, opens the overlay (and emits only if alwaysEmitClick is true).
   */
  @Output() buttonClick: EventEmitter<PointerEvent> = new EventEmitter();

  /**
   * @deprecated Use `(buttonClick)` instead. Named `click` conflicts with the native DOM click event.
   */
  @Output() click: EventEmitter<PointerEvent> = new EventEmitter();

  iconPosition: 'prefix' | 'suffix' = 'prefix';

  /** Material palette on the inner `mat-icon-button`; omit when `color` is unset so default icon tint applies. */
  get iconMatButtonColor(): 'primary' | 'basic' | 'accent' | 'warn' | 'success' | undefined {
    return this.color;
  }

  /** Legacy `lucide-icon` stroke when `type="icon"` and no palette `color` is set. */
  get lucideIconStroke(): string {
    return this.color ? 'black' : TsButtonComponent.iconDefaultColor;
  }

  constructor(readonly viewContainerRef: ViewContainerRef) {}

  /**
   * @description
   * Emit the buttonClick event if the button is not disabled or loading
   * @example
   * <ts-button (buttonClick)="onButtonClick($event)"></ts-button>
   **/
  onClick(event: PointerEvent): void {
    if (this.disabled || this.loading) return;

    if (this.tsOverlayTriggerFor) {
      this.openOverlayHelper(event);

      if (this.alwaysEmitClick) {
        this.emitButtonClick(event);
      }
    } else {
      this.emitButtonClick(event);
    }
  }

  private emitButtonClick(event: PointerEvent): void {
    this.buttonClick.emit(event);
    this.click.emit(event);
  }

  openOverlayHelper(event: any): void {
    this.tsOverlayTriggerFor.displayOverlay(event.target?.nativeElement || event.target);
  }
}
